import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Lock, RefreshCw, Phone, Mail, Calendar, User, MessageSquare, LogOut, Archive, Trash2, RotateCcw, Eye, EyeOff, Home } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError("");
      localStorage.setItem("adminAuth", "true");
    } else {
      setError("Incorrect password");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("adminAuth");
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/contact?page_size=100`),
        axios.get(`${API}/contact/archived?page_size=100`)
      ]);
      
      // Handle paginated response
      const activeItems = activeRes.data.items || activeRes.data;
      const archivedItems = archivedRes.data.items || archivedRes.data;
      
      setLeads(Array.isArray(activeItems) ? activeItems : []);
      setArchivedLeads(Array.isArray(archivedItems) ? archivedItems : []);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/contact/${id}?status=${newStatus}`);
      toast.success(newStatus === "archived" ? "Lead archived" : "Status updated");
      fetchLeads();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`);
      toast.success("Lead deleted permanently");
      setDeleteDialogOpen(false);
      setLeadToDelete(null);
      fetchLeads();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  const confirmDelete = (lead) => {
    setLeadToDelete(lead);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    if (localStorage.getItem("adminAuth") === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "new":
        return <Badge className="bg-blue-500 hover:bg-blue-600">New</Badge>;
      case "contacted":
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Contacted</Badge>;
      case "quoted":
        return <Badge className="bg-purple-500 hover:bg-purple-600">Quoted</Badge>;
      case "booked":
        return <Badge className="bg-green-500 hover:bg-green-600">Booked</Badge>;
      case "completed":
        return <Badge className="bg-emerald-600 hover:bg-emerald-700">Completed</Badge>;
      case "archived":
        return <Badge className="bg-gray-500 hover:bg-gray-600">Archived</Badge>;
      default:
        return <Badge className="bg-gray-500">{status}</Badge>;
    }
  };

  const getServiceLabel = (service) => {
    const labels = {
      doors: "Doors",
      trimwork: "Trimwork",
      panelling: "Panelling",
      multiple: "Multiple",
      carpentry: "Carpentry",
      both: "Multiple"
    };
    return labels[service] || service;
  };

  const displayLeads = showArchived ? archivedLeads : leads;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
        <Helmet>
          <title>Admin Login | Expert Carpenter</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <Card className="w-full max-w-md p-8 bg-white">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#2D5A4A] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#3A3A3A]">Admin Login</h1>
            <p className="text-gray-500 mt-2">Enter password to view leads</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                data-testid="admin-password-input"
              />
            </div>
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            <Button type="submit" className="w-full h-12 bg-[#2D5A4A] hover:bg-[#234839]" data-testid="admin-login-btn">
              Login
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Helmet>
        <title>Lead Dashboard | Expert Carpenter</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the lead from {leadToDelete?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteLead(leadToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="bg-[#2D5A4A] text-white py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Lead Dashboard</h1>
            <p className="text-white/70 text-xs sm:text-sm">Expert Carpenter</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Link to="/">
              <Button
                variant="outline"
                size="sm"
                className="border-white/30 text-white hover:bg-white/10 h-9 px-3"
              >
                <Home className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Website</span>
              </Button>
            </Link>
            <Button
              onClick={() => setShowArchived(!showArchived)}
              variant="outline"
              size="sm"
              className={`border-white/30 hover:bg-white/10 h-9 px-3 ${showArchived ? 'bg-white/20 text-white' : 'text-white'}`}
            >
              {showArchived ? <Eye className="h-4 w-4 sm:mr-2" /> : <EyeOff className="h-4 w-4 sm:mr-2" />}
              <span className="hidden sm:inline">{showArchived ? "Active" : "Archived"}</span>
            </Button>
            <Button
              onClick={fetchLeads}
              variant="outline"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10 h-9 px-3"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10 h-9 px-3"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="p-4 sm:p-6">
            <p className="text-gray-500 text-xs sm:text-sm">Total Active</p>
            <p className="text-2xl sm:text-3xl font-bold text-[#3A3A3A]">{leads.length}</p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="text-gray-500 text-xs sm:text-sm">New</p>
            <p className="text-2xl sm:text-3xl font-bold text-blue-500">
              {leads.filter(l => l.status === "new").length}
            </p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="text-gray-500 text-xs sm:text-sm">Contacted</p>
            <p className="text-2xl sm:text-3xl font-bold text-yellow-500">
              {leads.filter(l => l.status === "contacted").length}
            </p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="text-gray-500 text-xs sm:text-sm">Booked</p>
            <p className="text-2xl sm:text-3xl font-bold text-green-500">
              {leads.filter(l => l.status === "booked").length}
            </p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="text-gray-500 text-xs sm:text-sm">Archived</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-500">{archivedLeads.length}</p>
          </Card>
        </div>

        {/* Leads Table */}
        <Card className="overflow-hidden">
          <div className="p-4 sm:p-6 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold text-[#3A3A3A]">
              {showArchived ? "Archived Leads" : "Active Leads"}
            </h2>
            <span className="text-sm text-gray-500">
              {displayLeads.length} {displayLeads.length === 1 ? "lead" : "leads"}
            </span>
          </div>
          
          {displayLeads.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{showArchived ? "No archived leads" : "No active leads yet"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead className="hidden md:table-cell">Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="h-4 w-4 hidden sm:block" />
                          <span className="text-xs sm:text-sm">{formatDate(lead.timestamp)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          <User className="h-4 w-4 text-gray-400 hidden sm:block" />
                          {lead.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-[#2D5A4A] hover:underline">
                            <Phone className="h-4 w-4" />
                            <span className="hidden sm:inline">{lead.phone}</span>
                            <span className="sm:hidden">Call</span>
                          </a>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-gray-500 hover:underline">
                              <Mail className="h-4 w-4" />
                              <span className="hidden md:inline">{lead.email}</span>
                              <span className="md:hidden">Email</span>
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getServiceLabel(lead.service_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs hidden md:table-cell">
                        <p className="text-sm text-gray-600 truncate" title={lead.message}>
                          {lead.message}
                        </p>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(lead.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {!showArchived && (
                            <>
                              {lead.status === "new" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(lead.id, "contacted")}
                                  className="text-xs h-8"
                                >
                                  Contacted
                                </Button>
                              )}
                              {lead.status === "contacted" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(lead.id, "quoted")}
                                  className="text-xs h-8"
                                >
                                  Quoted
                                </Button>
                              )}
                              {lead.status === "quoted" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(lead.id, "booked")}
                                  className="text-xs h-8"
                                >
                                  Booked
                                </Button>
                              )}
                              {lead.status === "booked" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(lead.id, "completed")}
                                  className="text-xs h-8"
                                >
                                  Done
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateStatus(lead.id, "archived")}
                                className="text-xs h-8 text-gray-500 hover:text-gray-700"
                                title="Archive"
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {showArchived && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateStatus(lead.id, "new")}
                              className="text-xs h-8"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Restore
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => confirmDelete(lead)}
                            className="text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete permanently"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminPage;
